// Request history store — keeps last 10 try-it calls in localStorage.
// FIFO: newest first; addEntry pushes to head and trims to MAX.

import { create } from 'zustand';

const STORAGE_KEY = 'playground_request_history';
const MAX = 10;

export interface HistoryItem {
    id: string;
    operationId?: string;
    method: string;
    path: string;
    /** Full URL hit (with substituted params + query string). Useful for replay. */
    url?: string;
    status: number;
    elapsedMs: number;
    ts: number;
    /** Persist enough info to replay (form snapshot). */
    pathParams?: Record<string, string>;
    queryParams?: Record<string, string>;
    body?: string;
}

interface State {
    history: HistoryItem[];
    addEntry: (item: Omit<HistoryItem, 'id' | 'ts'> & { id?: string; ts?: number }) => void;
    clearAll: () => void;
    loadFromStorage: () => void;
}

function persist(items: HistoryItem[]) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }
    catch { /* private mode / quota */ }
}

function genId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export const useHistoryStore = create<State>((set, get) => ({
    history: [],
    addEntry: (item) => {
        const entry: HistoryItem = {
            id: item.id ?? genId(),
            ts: item.ts ?? Date.now(),
            operationId: item.operationId,
            method: item.method,
            path: item.path,
            url: item.url,
            status: item.status,
            elapsedMs: item.elapsedMs,
            pathParams: item.pathParams,
            queryParams: item.queryParams,
            body: item.body,
        };
        const next = [entry, ...get().history].slice(0, MAX);
        persist(next);
        set({ history: next });
    },
    clearAll: () => {
        try { localStorage.removeItem(STORAGE_KEY); }
        catch { /* */ }
        set({ history: [] });
    },
    loadFromStorage: () => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                set({ history: parsed.slice(0, MAX) });
            }
        } catch { /* corrupted — ignore */ }
    },
}));

export const HISTORY_MAX = MAX;
export const HISTORY_STORAGE_KEY = STORAGE_KEY;
